import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Image, Video, Plus, Loader2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { formatDistanceToNow } from 'date-fns';
import { getOptimizedImageUrl } from '@/utils/imageUtils';

interface DishGalleryContentProps {
  dishId: string;
  dishName?: string;
  showHeader?: boolean;
  onAddContent?: () => void;
  disableInternalScroll?: boolean;
}

interface GalleryPost {
  id: string;
  content: string | null;
  image_url: string | null;
  image_urls: string[] | null;
  video_url: string | null;
  media_type: 'image' | 'video' | null;
  created_at: string;
  user_id: string;
  profiles: {
    id: string;
    username: string;
    avatar_url: string | null;
  } | null;
}

export const DishGalleryContent: React.FC<DishGalleryContentProps> = ({
  dishId,
  dishName,
  showHeader = false,
  onAddContent,
  disableInternalScroll = false,
}) => {
  const [selectedPost, setSelectedPost] = useState<GalleryPost | null>(null);
  const [overlayImageIndex, setOverlayImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'all' | 'image' | 'video'>('all');

  const openPost = (post: GalleryPost) => {
    setSelectedPost(post);
    setOverlayImageIndex(0);
  };

  // Fetch posts and profiles separately, then merge (bypasses FK/join issues)
  const { data: posts, isLoading } = useQuery({
    queryKey: ['dish-gallery', dishId],
    queryFn: async () => {
      // Step 1: Fetch posts for this dish
      const { data: postsData, error: postsError } = await supabase
        .from('posts')
        .select('id, content, image_url, image_urls, video_url, media_type, created_at, user_id')
        .eq('dish_id', dishId)
        .order('created_at', { ascending: false });

      if (postsError) {
        console.error('Posts fetch error:', postsError);
        throw postsError;
      }
      
      if (!postsData || postsData.length === 0) {
        return [];
      }

      // Step 2: Get unique user IDs and batch fetch profiles
      const userIds = [...new Set(postsData.map(p => p.user_id).filter(Boolean))];
      
      let profilesMap = new Map<string, { id: string; username: string; avatar_url: string | null }>();
      
      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, avatar_url')
          .in('id', userIds);

        if (profilesError) {
          console.error('Profiles fetch error:', profilesError);
          // Don't throw - continue without profiles
        } else if (profilesData) {
          profilesMap = new Map(profilesData.map(p => [p.id, p]));
        }
      }

      // Step 3: Merge posts with profiles
      return postsData.map(post => ({
        ...post,
        profiles: post.user_id ? profilesMap.get(post.user_id) || null : null
      })) as GalleryPost[];
    },
    enabled: !!dishId
  });

  // Filter posts by media type
  const filteredPosts = posts?.filter(post => {
    if (activeTab === 'all') return true;
    if (activeTab === 'image') return post.media_type === 'image' || post.image_url;
    if (activeTab === 'video') return post.media_type === 'video' || post.video_url;
    return true;
  }) || [];

  const imageCount = posts?.reduce((n, p) => n + (p.image_urls?.length || (p.image_url ? 1 : 0)), 0) || 0;
  const videoCount = posts?.filter(p => p.media_type === 'video' || p.video_url).length || 0;

  // Flatten image posts into one item per image so each gets its own grid tile
  const displayItems = filteredPosts.flatMap(post => {
    if (post.video_url) return [{ post, url: null as string | null, idx: 0 }];
    const imgs = post.image_urls?.length ? post.image_urls : post.image_url ? [post.image_url] : [];
    return imgs.map((url, idx) => ({ post, url, idx }));
  });

  return (
    <>
    {/* Fullscreen media overlay */}
    <AnimatePresence>
      {selectedPost && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ type: 'tween', duration: 0.2, ease: 'easeOut' }}
          drag="y"
          dragConstraints={{ top: 0 }}
          dragElastic={{ top: 0, bottom: 0.4 }}
          onDragEnd={(_, info) => { if (info.offset.y > 100) setSelectedPost(null); }}
          className="fixed inset-0 z-[100] bg-black flex flex-col touch-none"
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-2 pb-0">
            <div className="w-10 h-1 bg-white/30 rounded-full" />
          </div>

          {/* Header */}
          <div
            className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-black/60 backdrop-blur-sm"
            style={{ paddingTop: 'calc(env(safe-area-inset-top) + 4px)' }}
          >
            <button
              onClick={() => setSelectedPost(null)}
              className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Avatar className="w-7 h-7">
                <AvatarImage src={selectedPost.profiles?.avatar_url || ''} />
                <AvatarFallback className="bg-gradient-to-r from-emerald-500 to-violet-500 text-white text-[10px]">
                  {selectedPost.profiles?.username?.substring(0, 2).toUpperCase() || 'US'}
                </AvatarFallback>
              </Avatar>
              <div className="text-right">
                <p className="text-white text-sm font-medium leading-tight">
                  {selectedPost.profiles?.username || 'Anonymous'}
                </p>
                <p className="text-white/50 text-xs">
                  {formatDistanceToNow(new Date(selectedPost.created_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          </div>

          {/* Media */}
          <div className="flex-1 relative">
            {selectedPost.video_url ? (
              <video
                src={selectedPost.video_url}
                controls
                autoPlay
                className="absolute inset-0 w-full h-full object-contain"
              />
            ) : (() => {
              const images = selectedPost.image_urls?.length
                ? selectedPost.image_urls
                : selectedPost.image_url
                  ? [selectedPost.image_url]
                  : [];
              const activeImg = images[overlayImageIndex];
              if (!activeImg) return null;
              return (
                <>
                  {/* Blurred atmospheric background */}
                  <img
                    src={getOptimizedImageUrl(activeImg, 50, 50)}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover blur-3xl opacity-50"
                  />
                  {/* Full-res foreground */}
                  <img
                    src={getOptimizedImageUrl(activeImg, 800, 1200)}
                    alt={selectedPost.content || 'Post image'}
                    className="relative w-full h-full object-contain z-10"
                  />
                  {/* Prev / next buttons */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setOverlayImageIndex(i => Math.max(0, i - 1))}
                        disabled={overlayImageIndex === 0}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white disabled:opacity-30"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setOverlayImageIndex(i => Math.min(images.length - 1, i + 1))}
                        disabled={overlayImageIndex === images.length - 1}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/50 text-white disabled:opacity-30"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                      {/* Dot indicators */}
                      <div className="absolute bottom-3 left-0 right-0 z-20 flex justify-center gap-1.5">
                        {images.map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setOverlayImageIndex(i)}
                            className={`w-1.5 h-1.5 rounded-full transition-colors ${i === overlayImageIndex ? 'bg-white' : 'bg-white/40'}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>

          {/* Caption */}
          {selectedPost.content && (
            <div className="flex-shrink-0 px-4 py-3 bg-black/60 backdrop-blur-sm">
              <p className="text-white/80 text-sm">{selectedPost.content}</p>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>

    <div className={`flex flex-col ${disableInternalScroll ? '' : 'h-full'}`}>
      {/* Optional Header */}
      {showHeader && dishName && (
        <div className="p-4 border-b border-border">
          <h1 className="text-lg font-bold text-foreground truncate">
            {dishName}
          </h1>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'all' | 'image' | 'video')} className="flex-1 flex flex-col">
        <TabsList className="mx-4 mt-2 bg-muted border border-border">
          <TabsTrigger
            value="all"
            className="flex-1 data-[state=active]:bg-[#cfef00] data-[state=active]:text-black text-muted-foreground"
          >
            All ({imageCount + videoCount})
          </TabsTrigger>
          <TabsTrigger
            value="image"
            className="flex-1 data-[state=active]:bg-[#cfef00] data-[state=active]:text-black text-muted-foreground"
          >
            <Image className="h-4 w-4 mr-1" />
            Snaps ({imageCount})
          </TabsTrigger>
          <TabsTrigger
            value="video"
            className="flex-1 data-[state=active]:bg-[#cfef00] data-[state=active]:text-black text-muted-foreground"
          >
            <Video className="h-4 w-4 mr-1" />
            Drools ({videoCount})
          </TabsTrigger>
        </TabsList>

        {/* Content */}
        <div className={`${disableInternalScroll ? '' : 'flex-1 overflow-y-auto'} p-4`}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#cfef00]" />
            </div>
          ) : displayItems.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                {activeTab === 'video' ? (
                  <Video className="h-10 w-10 text-muted-foreground" />
                ) : (
                  <Image className="h-10 w-10 text-muted-foreground" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No community content yet
              </h3>
              <p className="text-muted-foreground text-sm mb-6 max-w-xs">
                Be the first to share a {activeTab === 'video' ? 'drool' : 'snap'} of this dish!
              </p>
              {onAddContent && (
                <Button
                  onClick={onAddContent}
                  className="bg-[#cfef00] hover:bg-[#cfef00]/90 text-black"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Content
                </Button>
              )}
            </div>
          ) : (
            /* Gallery Grid */
            <div className="grid grid-cols-3 gap-2">
              {displayItems.map((item) => (
                <button
                  key={`${item.post.id}-${item.idx}`}
                  onClick={() => { openPost(item.post); setOverlayImageIndex(item.idx); }}
                  className="aspect-square rounded-lg overflow-hidden bg-muted hover:ring-2 hover:ring-[#cfef00] transition-all relative group"
                >
                  {item.post.video_url ? (
                    <>
                      <video
                        src={item.post.video_url}
                        className="w-full h-full object-cover"
                        muted
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <Video className="h-6 w-6 text-white" />
                      </div>
                    </>
                  ) : item.url ? (
                    <img
                      src={item.url}
                      alt={item.post.content || 'Post'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Image className="h-6 w-6" />
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                    <div className="flex items-center space-x-2">
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={item.post.profiles?.avatar_url || ''} />
                        <AvatarFallback className="text-[10px] bg-white/20 text-white">
                          {item.post.profiles?.username?.substring(0, 2).toUpperCase() || 'US'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-white text-xs truncate">
                        {item.post.profiles?.username || 'Anonymous'}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </Tabs>

      {/* Footer - Add Content CTA */}
      {!isLoading && filteredPosts.length > 0 && onAddContent && (
        <div className="p-4 border-t border-border">
          <Button
            onClick={onAddContent}
            variant="outline"
            className="w-full border-[#cfef00]/50 text-[#cfef00] hover:bg-[#cfef00]/10"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your Snap or Drool
          </Button>
        </div>
      )}
    </div>
    </>
  );
};
